// ./src/components/generic/form/SegmentedField.tsx
//
// SegmentedControl wrapper on the shared `(name, value)` change contract. Handy
// for small mutually-exclusive choices where a dropdown would be a click too many.
//
// On phones a horizontal segmented control with several options — or a few with
// long labels — overflows: the last options get cut off / become unreachable (a
// segmented control doesn't scroll). So when they would overflow a narrow screen
// we stack them vertically (each option full-width and tappable). A caller can
// force a specific `orientation` to opt out.
//
// ⚠ A FULL-WIDTH control MEASURES whether its options fit (owner, 2026-10-09): the character estimate below is sized
//   for a 375px phone, so on a 430px one (iPhone 15 Pro Max) "Always | Before | During | After" stacked into four
//   rows although it fits in one. A full-width control knows its row — its container — so it lays the options out
//   once, hidden, at their natural width, and stacks only when that is wider than the row. A control that is not
//   full-width has no row to measure against and keeps the estimate.

import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Input, SegmentedControl, SegmentedControlProps } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { FieldChangeHandler } from './types';

// At/above this many options a horizontal row stops fitting comfortably on a phone.
const VERTICAL_ON_MOBILE_THRESHOLD = 4;
// …and FEWER options with long labels do not fit either: "Everyone on the page | Guests | Some guests" cut its last option
// off at 375px (2026-10-04). A phone stacks them too once the labels' characters PLUS each segment's padding (counted as
// a few characters per option) pass what a 343px row holds. "Everyone | Guests | Some guests" (34) fits;
// "Airport or station | Venue | Address" (39) does not.
const ROW_CHARS_ON_MOBILE = 36;
const PADDING_CHARS_PER_OPTION = 3;
// A label that is not plain text (an icon, a node) is counted as this many characters.
const NODE_LABEL_CHARS = 10;
const MOBILE_MEDIA_QUERY = '(max-width: 48em)';

/** How many characters the options' labels take, all together. */
const labelChars = (data: SegmentedControlProps['data']): number =>
  (Array.isArray(data) ? data : []).reduce<number>((sum, item) => {
    const label = typeof item === 'string' ? item : item?.label;

    return sum + (typeof label === 'string' ? label.length : NODE_LABEL_CHARS);
  }, 0);

/**
 * A full-width control's measurement (px): its ROW, what a row of its options needs with each option as wide as its
 * own text (`natural`), and what it needs when every option gets an EQUAL share (`equal`, Mantine's own full-width
 * layout: its segments are `flex: 1`, so the row must hold the WIDEST option once per option).
 */
export interface SegmentedFit {
  row: number;
  natural: number;
  equal: number;
}

/**
 * Both widths, from a hidden copy laid out at its natural width — "Everyone on the page | Guests | Some guests" fits
 * by its total width (natural) long before its first option fits in a third of the row (equal).
 */
export const measureRow = (probe: HTMLElement): { natural: number; equal: number } => {
  const root = probe.firstElementChild as HTMLElement | null;

  if (!root) {
    return { natural: 0, equal: 0 };
  }

  const rootWidth = root.getBoundingClientRect().width;
  const segments = Array.from(root.querySelectorAll<HTMLElement>(':scope > .mantine-SegmentedControl-control')).map((el) => el.getBoundingClientRect().width);

  if (segments.length === 0) {
    return { natural: Math.ceil(rootWidth), equal: Math.ceil(rootWidth) };
  }

  const chrome = rootWidth - segments.reduce((sum, w) => sum + w, 0);

  return { natural: Math.ceil(rootWidth), equal: Math.ceil(chrome + segments.length * Math.max(...segments)) };
};

/**
 * How a measured row shares its width: EQUAL shares (Mantine's full-width look) when they fit, else each option as wide
 * as its text — a row that fits only that way stays one row instead of stacking (2026-10-09: on a 430px phone the
 * block's "who" fitted by 3px less than that).
 */
export const segmentedShares = (fit: SegmentedFit | null | undefined): 'equal' | 'content' =>
  fit && fit.row > 0 && fit.equal > fit.row ? 'content' : 'equal';

// A measured row's side padding on a phone (Mantine's md is 14px): enough to tap, and a little more of the row for text.
const PHONE_SEGMENT_PADDING = 10;

/**
 * The orientation to draw: the caller's, if it chose one; never vertical on a desktop; on a phone, when the options
 * were MEASURED against their row, vertical exactly when they do not fit in it; otherwise (not measured — not
 * full-width, or before the first layout) VERTICAL when there are many options or their labels are too long for a
 * 375px row; else horizontal.
 */
export const segmentedOrientation = (args: {
  isMobile: boolean | undefined;
  data: SegmentedControlProps['data'];
  orientation?: SegmentedControlProps['orientation'];
  fit?: SegmentedFit | null;
}): 'horizontal' | 'vertical' => {
  if (args.orientation) {
    return args.orientation;
  }

  if (!args.isMobile) {
    return 'horizontal';
  }

  if (args.fit && args.fit.row > 0 && args.fit.natural > 0) {
    return args.fit.natural > args.fit.row ? 'vertical' : 'horizontal';
  }

  const count = Array.isArray(args.data) ? args.data.length : 0;
  const tooMany = count >= VERTICAL_ON_MOBILE_THRESHOLD || labelChars(args.data) + PADDING_CHARS_PER_OPTION * count > ROW_CHARS_ON_MOBILE;

  return tooMany ? 'vertical' : 'horizontal';
};

export interface SegmentedFieldProps extends Omit<SegmentedControlProps, 'onChange'> {
  name?: string;
  onChange?: FieldChangeHandler<string>;
  /** Server-side error for this field — rendered like any other input's. */
  error?: React.ReactNode;
  label?: React.ReactNode;
  description?: React.ReactNode;
}

export const SegmentedField: React.FC<SegmentedFieldProps> = ({
  name,
  onChange,
  data,
  orientation,
  error,
  label,
  description,
  ...rest
}) => {
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const measure = !!isMobile && !orientation && !!rest.fullWidth;
  const rowRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<SegmentedFit | null>(null);

  // Before paint, and again whenever the row or the options' width changes (a rotated phone, a font that loads late).
  useLayoutEffect(() => {
    const row = rowRef.current;
    const probe = probeRef.current;

    if (!measure || !row || !probe) {
      setFit(null);

      return;
    }

    const read = () => {
      const next = { row: row.clientWidth, ...measureRow(probe) };

      setFit((prev) => (prev && prev.row === next.row && prev.natural === next.natural && prev.equal === next.equal ? prev : next));
    };

    read();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    observer?.observe(row);
    observer?.observe(probe);

    return () => observer?.disconnect();
  }, [measure, data, rest.size]);

  const effectiveOrientation = segmentedOrientation({ isMobile, data, orientation, fit: measure ? fit : null });
  const byContent = measure && effectiveOrientation === 'horizontal' && segmentedShares(fit) === 'content';
  const callerStyles = rest.styles && typeof rest.styles === 'object' ? (rest.styles as Record<string, React.CSSProperties>) : null;
  // The SAME label padding on the visible row and on the hidden one it is measured by, or the measure lies (a caller's
  // styles FUNCTION cannot be merged, so that control keeps Mantine's padding in both).
  const phoneLabel = measure && (!rest.styles || callerStyles) ? { paddingInline: PHONE_SEGMENT_PADDING } : undefined;
  const styles = phoneLabel
    ? {
        ...callerStyles,
        label: { ...callerStyles?.label, ...phoneLabel },
        ...(byContent ? { control: { ...callerStyles?.control, flex: '1 1 auto' } } : {}),
      }
    : rest.styles;

  const visible = (
    <SegmentedControl
      data={data}
      orientation={effectiveOrientation}
      onChange={(v) => onChange?.(name, v)}
      {...rest}
      styles={styles}
    />
  );

  // The measured layout: the row, plus the same options laid out once in a row at their natural width — hidden, out of
  // the tab order and the accessibility tree, and in a 0×0 box so it can never widen the page.
  const control = measure ? (
    <Box ref={rowRef} style={{ position: 'relative' }}>
      {visible}
      <Box aria-hidden style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none' }}>
        <Box ref={probeRef} style={{ width: 'max-content' }}>
          <SegmentedControl data={data} size={rest.size} radius={rest.radius} value={rest.value} orientation="horizontal" styles={{ label: { ...callerStyles?.label, ...phoneLabel } }} />
        </Box>
      </Box>
    </Box>
  ) : (
    visible
  );

  // Wrap only when there's something to wrap WITH — a bare control keeps its
  // inline-flex layout. The control's root is inline-flex, so it goes in a Box
  // (see em-scaffold-ui) or the label would sit beside it.
  if (!label && !description && !error) {
    return control;
  }

  return (
    <Input.Wrapper label={label} description={description} error={error} size={rest.size ?? 'sm'}>
      <Box>{control}</Box>
    </Input.Wrapper>
  );
};
